import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ShortNote } from '../../../../../api/homeShortsFeeds/types';
import { Components } from '../styled';
import { formatDuration, isNewShort, moveId, ShortRow, sortIdsByWorth, isNoteEmpty, worthColor } from '../utils';

// Edits one short's worth / why / gold star. Local state until Save.
const NotesDialog = ({
  short,
  note,
  onClose,
  onSave,
}: {
  short?: ShortRow;
  note?: ShortNote;
  onClose: () => void;
  onSave: (id: string, note: ShortNote) => void;
}) => {
  const [worth, setWorth] = useState('');
  const [why, setWhy] = useState('');
  const [goldStar, setGoldStar] = useState(false);

  useEffect(() => {
    if (short) {
      setWorth(note?.worth != null ? String(note.worth) : '');
      setWhy(note?.why ?? '');
      setGoldStar(!!note?.goldStar);
    }
    // Reset only when a different short is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [short?.id]);

  const parsed = parseFloat(worth);
  const hasWorth = !Number.isNaN(parsed);
  const isWorthInvalid = worth.trim() !== '' && (!hasWorth || parsed < 0 || parsed > 10);

  const save = () => {
    if (!short || isWorthInvalid) {
      return;
    }
    onSave(short.id, { worth: hasWorth ? parsed : null, why: why.trim(), goldStar });
    onClose();
  };

  return (
    <Dialog open={!!short} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle>Notes: {short?.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            size='small'
            type='number'
            label='Worth (0-10)'
            value={worth}
            error={isWorthInvalid}
            helperText={isWorthInvalid ? 'Enter a number from 0 to 10, or leave empty.' : 'Leave empty for no score.'}
            onChange={(e) => setWorth(e.target.value)}
            inputProps={{ min: 0, max: 10, step: 0.5 }}
          />
          <TextField
            size='small'
            label='Why'
            multiline
            minRows={2}
            value={why}
            onChange={(e) => setWhy(e.target.value)}
          />
          <FormControlLabel
            control={<Checkbox checked={goldStar} onChange={(e) => setGoldStar(e.target.checked)} />}
            label='Gold star'
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant='contained' onClick={save} disabled={isWorthInvalid}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Type a rank and press Enter (or leave the field) to move the row there.
const RankInput = ({
  rank,
  total,
  label,
  onCommit,
}: {
  rank: number;
  total: number;
  label: string;
  onCommit: (rank: number) => void;
}) => {
  const [value, setValue] = useState(String(rank));

  const commit = () => {
    const parsed = parseInt(value);
    if (Number.isNaN(parsed)) {
      setValue(String(rank));
      return;
    }
    const clamped = Math.min(Math.max(parsed, 1), total);
    setValue(String(clamped));
    if (clamped !== rank) {
      onCommit(clamped);
    }
  };

  return (
    <Components.RankInput
      type='number'
      min={1}
      max={total}
      value={value}
      aria-label={label}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
    />
  );
};

interface Props {
  title: string;
  isDefault: boolean;
  shortIds: string[];
  shortsById: Map<string, ShortRow>;
  /** admin-only editorial notes keyed by short id */
  notes?: Record<string, ShortNote>;
  onSaveNote?: (id: string, note: ShortNote) => void;
  onChange: (ids: string[]) => void;
  onOpenAdd: () => void;
  onOpenImport: () => void;
  onExport: () => void;
  onDownloadExample: () => void;
  /** how many Expert slots the app's Home feed has */
  homepageSlots: number;
}

// Right pane: the selected feed's ranked list. Index 0 is the first short a user sees.
const FeedEditor = ({
  title,
  isDefault,
  shortIds,
  shortsById,
  notes = {},
  onSaveNote,
  onChange,
  onOpenAdd,
  onOpenImport,
  onExport,
  onDownloadExample,
  homepageSlots,
}: Props) => {
  const [search, setSearch] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [draggedId, setDraggedId] = useState<string | undefined>(undefined);
  const [overId, setOverId] = useState<string | undefined>(undefined);
  const [goldOnly, setGoldOnly] = useState(false);
  const [notesId, setNotesId] = useState<string | undefined>(undefined);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const isFiltered = !!search.trim() || !!authorFilter || goldOnly;
  const hasAnyWorth = shortIds.some((id) => notes[id]?.worth != null);

  const mentors = useMemo(() => {
    const byId = new Map<string, string>();
    shortIds.forEach((id) => {
      const short = shortsById.get(id);
      if (short) {
        byId.set(short.authorId, short.authorName);
      }
    });
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [shortIds, shortsById]);

  const visibleIds = useMemo(() => {
    const term = search.trim().toLowerCase();
    return shortIds.filter((id) => {
      const short = shortsById.get(id);
      if (!short) {
        return false;
      }
      if (authorFilter && short.authorId !== authorFilter) {
        return false;
      }
      if (goldOnly && !notes[id]?.goldStar) {
        return false;
      }
      return !term || `${short.title} ${short.authorName} ${short.id}`.toLowerCase().includes(term);
    });
  }, [shortIds, shortsById, search, authorFilter, goldOnly, notes]);

  const resetDrag = () => {
    setDraggedId(undefined);
    setOverId(undefined);
  };

  const total = shortIds.length;
  const notesShort = notesId ? shortsById.get(notesId) : undefined;

  return (
    <Components.Pane aria-label={`Ranked shorts for ${title}`}>
      <Components.PaneHeader>
        <Box>
          <Typography variant='subtitle1' component='h2'>
            {title}
          </Typography>
          <Components.Meta>
            {total} shorts · each user sees their next {homepageSlots} unwatched shorts in this order, then starts again
            from rank 1
          </Components.Meta>
        </Box>
        <Components.Actions>
          <Components.ActionButton type='button' onClick={onOpenAdd}>
            Add shorts
          </Components.ActionButton>
          <Components.ActionButton type='button' onClick={onOpenImport}>
            Import CSV
          </Components.ActionButton>
          <Components.ActionButton
            type='button'
            onClick={() => setIsSortOpen(true)}
            disabled={isFiltered || !hasAnyWorth}
            title={isFiltered ? 'Clear the filter to sort' : !hasAnyWorth ? 'No short has a worth score' : undefined}>
            Sort by worth
          </Components.ActionButton>
          <Components.ActionButton type='button' onClick={onExport} disabled={total === 0}>
            Export current order
          </Components.ActionButton>
          <Components.ActionButton type='button' onClick={onDownloadExample}>
            Download example CSV
          </Components.ActionButton>
        </Components.Actions>
      </Components.PaneHeader>

      <Box sx={{ px: 2, pt: 1, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant='caption' color='text.secondary' component='p' sx={{ mb: 1 }}>
          CSV needs a <code>short_id</code> column. An optional <code>position</code> column sorts the rows; optional{' '}
          <code>worth</code>, <code>why</code> and <code>gold_star</code> columns fill the notes; other columns are
          ignored. Importing only changes the draft until you save.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField
            size='small'
            label='Search title, mentor or ID'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <FormControl size='small' sx={{ minWidth: 180 }}>
            <InputLabel id='feed-mentor-filter-label'>Mentor</InputLabel>
            <Select
              labelId='feed-mentor-filter-label'
              label='Mentor'
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}>
              <MenuItem value=''>All mentors</MenuItem>
              {mentors.map((mentor) => (
                <MenuItem key={mentor.id} value={mentor.id}>
                  {mentor.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={goldOnly} onChange={(e) => setGoldOnly(e.target.checked)} />}
            label='Gold star only'
          />
        </Box>
        {isFiltered && (
          <Components.Meta as='p'>
            Drag is disabled while a filter is applied. Ranks shown are the real positions.
          </Components.Meta>
        )}
      </Box>

      {total === 0 ? (
        <Components.Empty>
          {isDefault ? 'The default feed is empty.' : 'This feed has no shorts.'} Use “Add shorts” or import a CSV.
        </Components.Empty>
      ) : visibleIds.length === 0 ? (
        <Components.Empty>No shorts match the filter.</Components.Empty>
      ) : (
        <Components.List>
          {visibleIds.map((id) => {
            const short = shortsById.get(id) as ShortRow;
            const index = shortIds.indexOf(id);
            const rank = index + 1;
            const note = notes[id];
            return (
              <Components.Row
                key={id}
                draggable={!isFiltered}
                isDimmed={!short.isActive}
                isDragging={draggedId === id}
                isDropTarget={!!draggedId && overId === id && draggedId !== id}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', id);
                  setDraggedId(id);
                }}
                onDragOver={(e) => {
                  if (isFiltered) {
                    return;
                  }
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setOverId(id);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (draggedId && !isFiltered) {
                    onChange(moveId(shortIds, draggedId, index));
                  }
                  resetDrag();
                }}
                onDragEnd={resetDrag}>
                <Components.Handle
                  aria-hidden='true'
                  isDisabled={isFiltered}
                  title={isFiltered ? 'Clear the filter to drag' : 'Drag to reorder'}>
                  ⋮⋮
                </Components.Handle>
                <Components.Rank>#{rank}</Components.Rank>
                <Components.RowBody>
                  <Components.RowTitle title={short.title}>{short.title}</Components.RowTitle>
                  <Components.Meta>
                    {short.authorName || 'Unknown mentor'} · {formatDuration(short.durationSeconds)}
                  </Components.Meta>
                  {note?.why && <Components.Why title={note.why}>{note.why}</Components.Why>}
                  {!short.isActive && <Components.Meta>The app skips inactive shorts.</Components.Meta>}
                </Components.RowBody>
                {note?.worth != null && (
                  <Chip
                    size='small'
                    color={worthColor(note.worth)}
                    label={`Worth ${note.worth}`}
                    onClick={() => setNotesId(id)}
                    aria-label={`Worth ${note.worth}, edit notes for ${short.title}`}
                  />
                )}
                {note?.goldStar && (
                  <Chip size='small' color='warning' icon={<span aria-hidden='true'>★</span>} label='Gold star' />
                )}
                {isNewShort(short) && <Chip size='small' color='info' label='New' />}
                <Chip
                  size='small'
                  variant={short.isActive ? 'filled' : 'outlined'}
                  color={short.isActive ? 'success' : 'default'}
                  label={short.isActive ? 'Active' : 'Inactive'}
                />
                <Components.Actions>
                  <RankInput
                    key={`${id}-${rank}`}
                    rank={rank}
                    total={total}
                    label={`Rank of ${short.title}`}
                    onCommit={(next) => onChange(moveId(shortIds, id, next - 1))}
                  />
                  {onSaveNote && (
                    <Components.ActionButton
                      type='button'
                      aria-label={`${isNoteEmpty(note) ? 'Add notes for' : 'Edit notes for'} ${short.title}`}
                      style={isNoteEmpty(note) ? { opacity: 0.6, borderStyle: 'dashed' } : undefined}
                      onClick={() => setNotesId(id)}>
                      {isNoteEmpty(note) ? 'Add notes' : 'Notes'}
                    </Components.ActionButton>
                  )}
                  <Components.ActionButton
                    type='button'
                    aria-label={`Move ${short.title} to top`}
                    disabled={index === 0}
                    onClick={() => onChange(moveId(shortIds, id, 0))}>
                    Top
                  </Components.ActionButton>
                  <Components.ActionButton
                    type='button'
                    aria-label={`Move ${short.title} up`}
                    disabled={index === 0}
                    onClick={() => onChange(moveId(shortIds, id, index - 1))}>
                    ↑
                  </Components.ActionButton>
                  <Components.ActionButton
                    type='button'
                    aria-label={`Move ${short.title} down`}
                    disabled={index === total - 1}
                    onClick={() => onChange(moveId(shortIds, id, index + 1))}>
                    ↓
                  </Components.ActionButton>
                  <Components.ActionButton
                    type='button'
                    aria-label={`Remove ${short.title} from this feed`}
                    onClick={() => onChange(shortIds.filter((item) => item !== id))}>
                    Remove
                  </Components.ActionButton>
                </Components.Actions>
              </Components.Row>
            );
          })}
        </Components.List>
      )}

      {onSaveNote && (
        <NotesDialog
          short={notesShort}
          note={notesShort && notes[notesShort.id]}
          onClose={() => setNotesId(undefined)}
          onSave={onSaveNote}
        />
      )}

      <Dialog open={isSortOpen} onClose={() => setIsSortOpen(false)}>
        <DialogTitle>Sort by worth?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This replaces the current order in the draft. Highest worth first, gold star breaks ties, and shorts without
            a worth go last.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSortOpen(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={() => {
              onChange(sortIdsByWorth(shortIds, notes));
              setIsSortOpen(false);
            }}>
            Sort
          </Button>
        </DialogActions>
      </Dialog>
    </Components.Pane>
  );
};

export default FeedEditor;
